import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import BookingApi from "../../API/Apis/BookingApi";
import BookingGetters from "../../store/onlineBooking/getters";
import NotAuthorisedPage from "../notAuthorisedPage";
import { ShareboxPickupResponse } from "../../@types/DTO/Sharebox";
import D4Landing from "./D4Landing";
import D5ConfirmProfile from "./D5ConfirmProfile";
import D6ContractReview from "./D6ContractReview";
import D7Otp from "./D7Otp";
import D8OpenBox from "./D8OpenBox";
import D9Success from "./D9Success";
import D10ContractEmail from "./D10ContractEmail";
import D11Complete from "./D11Complete";

type Screen = "d4" | "d5" | "d6" | "d7" | "d8" | "d9" | "d10" | "d11";

export default function ShareboxCheckoutPage() {
  const { token: urlToken = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t: translate } = useTranslation();
  const appSettings = BookingGetters.getAppSettings();
  const [screen, setScreen] = useState<Screen>("d4");
  const [pickup, setPickup] = useState<ShareboxPickupResponse | null>(null);
  const [contact, setContact] = useState({ email: "", mobile: "" });
  const [rentalId, setRentalId] = useState("");
  const [movementId, setMovementId] = useState("");
  const [error, setError] = useState<"invalid" | "expired" | null>(null);
  const [loading, setLoading] = useState(true);
  const [skippingProfile, setSkippingProfile] = useState(false);
  const [flowComplete, setFlowComplete] = useState(false);

  const [activeToken, setActiveToken] = useState(urlToken);

  useEffect(() => {
    BookingApi.getShareboxPickup(urlToken)
      .then((res) => {
        setPickup(res.data);
        if (res.data.token) {
          setActiveToken(res.data.token);
        }
      })
      .catch((err) => {
        setError(
          err?.response?.data?.reason === "expired" ? "expired" : "invalid"
        );
      })
      .finally(() => setLoading(false));
  }, [urlToken]);

  if (!appSettings) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!appSettings.isShareboxEnabled) {
    return <NotAuthorisedPage />;
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error === "expired") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          textAlign: "center",
          padding: "24px",
        }}
      >
        <Typography variant="h6">
          {translate("This link has expired. Please contact support.")}
        </Typography>
      </Box>
    );
  }

  if (error || !pickup) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          textAlign: "center",
          padding: "24px",
        }}
      >
        <Typography variant="h6">
          {translate("This link is not valid. Please contact support.")}
        </Typography>
      </Box>
    );
  }

  const handleD4Next = (doneUpTo: number) => {
    if (doneUpTo >= 2) {
      setScreen("d8");
    } else if (doneUpTo === 1) {
      setSkippingProfile(true);
      const fetchContact = async (id: string) => {
        const response: any = await BookingApi.getCustomerDetails(Number(id));
        const fetched = response?.data || {};
        return {
          email: fetched.email || "",
          mobile: fetched.telephone || fetched.phNum1 || "",
        };
      };
      const resolveContact = async () => {
        if (pickup.driver1Id) {
          try {
            const driverContact = await fetchContact(pickup.driver1Id);
            if (driverContact.email) return driverContact;
          } catch (error) {
            console.error("Failed to fetch driver 1's contact details:", error);
          }
        }
        return fetchContact(pickup.customerId);
      };
      resolveContact()
        .then(setContact)
        .catch((error: any) => {
          console.error(
            "Failed to fetch the linked customer's contact details:",
            error
          );
        })
        .finally(() => {
          setSkippingProfile(false);
          setScreen("d6");
        });
    } else {
      setScreen("d5");
    }
  };

  if (screen === "d4") {
    if (skippingProfile) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    return (
      <D4Landing
        token={activeToken}
        pickup={pickup}
        onNext={handleD4Next}
        onRentalDetailsFetched={setMovementId}
        onRentalIdFetched={setRentalId}
        flowComplete={flowComplete}
      />
    );
  }

  if (screen === "d5") {
    return (
      <D5ConfirmProfile
        reservationId={pickup.reservationId}
        reservationCode={pickup.reservationCode}
        customerId={pickup.customerId}
        isCustPhysicalPerson={pickup.isCustPhysicalPerson}
        isCustIndividualCompany={pickup.isCustIndividualCompany}
        driver1Id={pickup.driver1Id}
        driver2Id={pickup.driver2Id}
        driver3Id={pickup.driver3Id}
        onComplete={(newContact) => {
          setContact(newContact);
          if (newContact.rentalId) {
            setRentalId(newContact.rentalId);
          }
          setPickup((prev) =>
            prev ? { ...prev, isWebCheckInDone: "true" } : prev
          );
          setScreen("d4");
        }}
        onExit={() => setScreen("d4")}
      />
    );
  }

  if (screen === "d6") {
    return (
      <D6ContractReview
        token={activeToken}
        pickup={pickup}
        onNext={() => setScreen("d7")}
        onBack={() => setScreen("d4")}
      />
    );
  }

  if (screen === "d7") {
    return (
      <D7Otp
        token={activeToken}
        email={contact.email}
        onVerified={() => {
          setPickup((prev) => (prev ? { ...prev, rezIsRental: "true" } : prev));
          setScreen("d4");
        }}
        onBack={() => setScreen("d6")}
      />
    );
  }

  if (screen === "d8") {
    return (
      <D8OpenBox
        token={activeToken}
        urlToken={urlToken}
        pickup={pickup}
        onOpened={() => setScreen("d9")}
        onBack={() => setScreen("d4")}
      />
    );
  }

  if (screen === "d9") {
    return (
      <D9Success
        pickup={pickup}
        onNext={() => setScreen("d10")}
        onBack={() => setScreen("d4")}
      />
    );
  }

  if (screen === "d10") {
    return (
      <D10ContractEmail
        token={activeToken}
        movementId={movementId}
        email={contact.email}
        pickup={pickup}
        onBack={() => setScreen("d4")}
        onDone={() => {
          setFlowComplete(true);
          setScreen("d11");
        }}
      />
    );
  }

  return (
    <D11Complete
      onComplete={() => setScreen("d4")}
      onBack={() => setScreen("d4")}
    />
  );
}
